import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  ActivePlatformAnnouncement,
  PLATFORM_ANNOUNCEMENT_TARGETS,
  PlatformAnnouncementService,
} from '../services/platform-announcement.service';
import { AuthService } from '../services/auth.service';

const DISMISSAL_KEY_PREFIX = 'scanafy.platform-announcement.dismissed:';

@Component({
  selector: 'app-platform-announcement-banner',
  standalone: true,
  imports: [CommonModule, TranslateModule, NzButtonModule, NzIconModule, NzToolTipModule],
  templateUrl: './platform-announcement-banner.component.html',
  styleUrl: './platform-announcement-banner.component.less',
})
export class PlatformAnnouncementBannerComponent implements OnInit, OnDestroy {
  announcement: ActivePlatformAnnouncement | null = null;
  private activeSubscription?: Subscription;
  private languageSubscription?: Subscription;

  constructor(
    private announcements: PlatformAnnouncementService,
    private router: Router,
    private translate: TranslateService,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.activeSubscription = this.announcements.active$.subscribe((announcement) => {
      this.announcement = announcement && !this.isDismissed(announcement._id) ? announcement : null;
    });
    this.languageSubscription = this.translate.onLangChange.subscribe(() => {
      this.announcement = this.announcement ? {...this.announcement} : null;
    });
  }

  ngOnDestroy(): void {
    this.activeSubscription?.unsubscribe();
    this.languageSubscription?.unsubscribe();
  }

  get message(): string {
    if (!this.announcement) return '';
    return this.translate.currentLang === 'en' && this.announcement.messageEn
      ? this.announcement.messageEn
      : this.announcement.messageDe;
  }

  get hasTarget(): boolean {
    return !!this.announcement?.targetKind;
  }

  get isSignOrderActionDisabled(): boolean {
    return this.announcement?.targetKind === 'qr_sign_order'
      && !this.hasRequiredSignOrderPermissions();
  }

  openTarget(): void {
    const targetKind = this.announcement?.targetKind;
    if (!targetKind || this.isSignOrderActionDisabled) return;
    const target = PLATFORM_ANNOUNCEMENT_TARGETS[targetKind];
    this.router.navigate([...target.commands], {
      queryParams: 'queryParams' in target ? target.queryParams : undefined,
    });
  }

  dismiss(): void {
    if (!this.announcement) return;
    try {
      localStorage.setItem(`${DISMISSAL_KEY_PREFIX}${this.announcement._id}`, '1');
    } catch {
      // The banner is still dismissed for the current view if storage is blocked.
    }
    this.announcement = null;
  }

  private isDismissed(id: string): boolean {
    try {
      return localStorage.getItem(`${DISMISSAL_KEY_PREFIX}${id}`) === '1';
    } catch {
      return false;
    }
  }

  private hasRequiredSignOrderPermissions(): boolean {
    return this.auth.hasPermission('company.sign_orders.create')
      && this.auth.hasPermission('company.properties.read');
  }
}
